import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  collection, query, where, getDocs, doc, updateDoc, orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import ListPagination from '../components/ListPagination';
import { confirmTeslimatDelivery } from '../utils/teslimatOnayService';
import { 
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Chip, CircularProgress, Dialog, 
  DialogActions, DialogContent, DialogContentText, DialogTitle,
  Grid, TextField, Divider, Card, CardContent, useMediaQuery, useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoIcon from '@mui/icons-material/Info';

function TeslimatListesi() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryParams = new URLSearchParams(location.search);
  const sube = queryParams.get('sube')?.trim();

  const [islemler, setIslemler] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 8;
  
  // Teslim Etme Onay Modalı için State
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedConfirmIslem, setSelectedConfirmIslem] = useState(null); 
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Detay ve Tarih Girme Modalı için State
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedIslem, setSelectedIslem] = useState(null);
  const [planlananTarih, setPlanlananTarih] = useState("");

  // --- VERİ ÇEKME ---
  const fetchData = async () => {
    if (!sube) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'subeler', sube, 'islemler'),
        where('teslimatDurumu', '==', 'Teslimat Bekliyor'),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setIslemler(data);
      setCurrentPage(0);
    } catch (error) {
      console.error("Veri çekme hatası: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sube]);

  // --- SATIRA TIKLAYINCA DETAY AÇMA ---
  const handleRowClick = (islem) => {
    setSelectedIslem(islem);
    setPlanlananTarih(islem.planlananTeslimTarihi || ""); 
    setOpenDetailModal(true);
  };

  // --- TARİHİ KAYDETME FONKSİYONU ---
  const handleDateSave = async () => {
    if (!selectedIslem || !sube) return;
    try {
      const islemRef = doc(db, 'subeler', sube, 'islemler', selectedIslem.id);
      
      await updateDoc(islemRef, {
        planlananTeslimTarihi: planlananTarih
      });

      alert("Tarih başarıyla kaydedildi.");
      setOpenDetailModal(false);
      fetchData(); 
    } catch (error) {
      console.error("Tarih kaydetme hatası:", error);
      alert("Tarih kaydedilirken bir hata oluştu.");
    }
  };

  // --- TESLİM ETME BUTONU (ONAY AÇMA) ---
  const handleTeslimEtClick = (e, islem) => {
    e.stopPropagation(); 
    setSelectedConfirmIslem(islem);
    setOpenConfirmDialog(true);
  };

  const confirmTeslimat = async () => {
    if (!selectedConfirmIslem || !sube) return;

    setConfirmLoading(true);
    try {
      const result = await confirmTeslimatDelivery(db, sube, selectedConfirmIslem.id);
      alert(result.message);

      if (result.success) {
        fetchData();
        setOpenConfirmDialog(false);
        setSelectedConfirmIslem(null);
      } else if (result.alreadyDone) {
        fetchData();
        setOpenConfirmDialog(false);
        setSelectedConfirmIslem(null);
      }
    } catch (error) {
      console.error('Teslimat hatası: ', error);
      alert('Hata oluştu: ' + error.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Yardımcı Format Fonksiyonu
  const formatCurrency = (val) => {
    return val ? parseFloat(val).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) + ' TL' : '0.00 TL';
  };

  if (loading) {
    return (
      <Box className="tl-page" sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const pageCount = Math.ceil(islemler.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentItems = islemler.slice(offset, offset + itemsPerPage);
  // stickyHeader başlık hücrelerine kendi arka planını verir; degrade ve alt çizgi burada korunur
  const headCellSx = { color: '#334155', borderBottom: '2px solid #c58a2f', background: 'linear-gradient(180deg, #fbfdff 0%, #f2f7ff 100%)' };

  return (
    <Box className="tl-page" sx={{ p: 2.5, background: 'radial-gradient(circle at 8% 8%, rgba(183, 125, 47, 0.14) 0%, transparent 30%), radial-gradient(circle at 95% 90%, rgba(15, 39, 68, 0.16) 0%, transparent 34%), linear-gradient(180deg, #f7f9fd 0%, #edf2f9 100%)', height: 'calc(100vh - 215px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      
      {/* BAŞLIK KARTI */}
      <Paper className="tl-header" elevation={0} sx={{ 
        px: 1.75, py: 1.1, mb: 1.5, borderRadius: 2.5, 
        background: 'linear-gradient(135deg, #102a43 0%, #1f3b63 68%, #b67924 100%)', 
        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
        boxShadow: '0 10px 20px rgba(15,23,42,0.14)', border: '1px solid rgba(255,255,255,0.16)'
      }}>
        <Box className="tl-header-main">
           <Typography
             variant="subtitle1"
             fontWeight="bold"
             sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '1.02rem', lineHeight: 1.25 }}
           >
             <LocalShippingIcon sx={{ fontSize: 20 }} /> TESLİMAT BEKLEYEN ÜRÜNLER
           </Typography>
           <Typography variant="caption" sx={{ opacity: 0.88, mt: 0.2, display: 'block', fontSize: '0.72rem', lineHeight: 1.3 }}>
             Detay görmek ve tarih girmek için satıra tıklayınız.
           </Typography>
        </Box>
        <Chip 
          className="tl-header-chip"
          label={`${islemler.length} Adet Bekleyen`} 
          color="warning" 
          sx={{ fontWeight: 700, fontSize: '0.78rem', height: 26, backgroundColor: 'rgba(255,255,255,0.92)', color: '#7c4a03', '& .MuiChip-label': { px: 1 } }} 
        />
      </Paper>

      {/* TABLO */}
      <TableContainer className="tl-table-wrap" component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'auto', border: '1px solid #d7e1ee', boxShadow: '0 10px 20px rgba(15, 23, 42, 0.08)', flex: '0 1 auto', minHeight: 0 }}>
        <Table
          className="tl-table"
          size="small"
          stickyHeader
          sx={{
            minWidth: 650,
            '& .MuiTableCell-root': { py: 1.6, px: 1.5, fontSize: '0.9rem', lineHeight: 1.35 },
            '& .MuiTableCell-head': { py: 1.2, fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.02em' }
          }}
        >
          <TableHead sx={{ background: 'linear-gradient(180deg, #fbfdff 0%, #f2f7ff 100%)' }}>
            <TableRow>
              <TableCell sx={headCellSx}>İŞLEM TARİHİ</TableCell>
              <TableCell sx={headCellSx}>MÜŞTERİ / FİRMA</TableCell>
              <TableCell sx={headCellSx}>ÜRÜN</TableCell>
              <TableCell sx={headCellSx}>MİKTAR</TableCell>
              <TableCell sx={headCellSx}>PLANLANAN TARİH</TableCell>
              <TableCell sx={headCellSx}>DURUM</TableCell>
              <TableCell align="right" sx={headCellSx}>AKSİYON</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentItems.length > 0 ? (
              currentItems.map((row) => {
                const musteriAdi = row.musteri 
                  ? `${row.musteri.isim} ${row.musteri.soyisim || ''}` 
                  : (row.firma ? row.firma.firmaIsmi : 'Belirsiz');

                const dateObj = row.timestamp?.toDate ? row.timestamp.toDate() : new Date();
                const tarihStr = dateObj.toLocaleDateString('tr-TR');
                
                let planlananStr = "-";
                if (row.planlananTeslimTarihi) {
                    const pDate = new Date(row.planlananTeslimTarihi);
                    planlananStr = pDate.toLocaleDateString('tr-TR');
                }

                return (
                  <TableRow 
                    className="tl-data-row"
                    key={row.id} 
                    hover 
                    onClick={() => handleRowClick(row)} 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:nth-of-type(odd)': { backgroundColor: '#fcfdff' }, '&:hover': { backgroundColor: '#edf5ff' } }}
                  >
                    <TableCell className="tl-c-tarih">
                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.82rem' }}>{tarihStr}</Typography>
                    </TableCell>
                    <TableCell className="tl-c-musteri">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon color="action" sx={{ fontSize: 16 }} />
                            <Typography component="span" variant="caption" sx={{ fontSize: '0.82rem' }}>{musteriAdi}</Typography>
                        </Box>
                    </TableCell>
                    <TableCell className="tl-c-urun" sx={{ color: '#1e40af', fontWeight: '500' }}>
                        {row.urun || row.kategori}
                    </TableCell>
                    <TableCell className="tl-c-miktar">
                        {row.gram ? `${row.gram} Gr` : ''} 
                        {row.gram && row.adet ? ' - ' : ''}
                        {row.adet ? `${row.adet} Adet` : ''}
                    </TableCell>
                    <TableCell className="tl-c-planlanan">
                        {row.planlananTeslimTarihi ? (
                            <Chip 
                                icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />} 
                                label={planlananStr} 
                                color="info" 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 26, '& .MuiChip-label': { px: 1, fontSize: '0.8rem' } }}
                            />
                        ) : (
                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem' }}>Belirlenmedi</Typography>
                        )}
                    </TableCell>
                    <TableCell className="tl-c-durum">
                        <Chip label="Bekliyor" color="error" size="small" sx={{ fontWeight: 700, height: 26, '& .MuiChip-label': { px: 1.1, fontSize: '0.8rem' } }} />
                    </TableCell>
                    <TableCell className="tl-c-aksiyon" align="right">
                        <Button 
                          className="tl-teslim-btn"
                          variant="contained" 
                          color="success" 
                          size="small"
                          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          onClick={(e) => handleTeslimEtClick(e, row)}
                          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', py: 0.6, px: 1.5, minHeight: 32, boxShadow: 1, borderRadius: '6px', background: 'linear-gradient(135deg, #1aa06f 0%, #0f7a55 100%)' }}
                        >
                          Teslim Et
                        </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="h6" color="textSecondary">
                    Teslimat bekleyen işlem bulunmamaktadır.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pageCount > 1 && (
        <ListPagination
          pageCount={pageCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={islemler.length}
          itemsPerPage={itemsPerPage}
          itemLabel="teslimat"
          detached
        />
      )}

      {/* --- KAPSAMLI DETAY VE TARİH GİRME MODALI --- */}
      <Dialog
        open={openDetailModal}
        onClose={() => setOpenDetailModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ className: 'tl-detail-dialog', sx: { borderRadius: isMobile ? 0 : 3, border: '1px solid #d9e3ef', boxShadow: '0 16px 30px rgba(15, 23, 42, 0.18)' } }}
      >
        <DialogTitle className="tl-detail-dialog-title" sx={{ background: 'linear-gradient(90deg, #132b4a 0%, #1e3657 58%, #b3681f 100%)', color: 'white', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                <ReceiptLongIcon /> 
                <Typography variant="h6">İşlem Detayları & Teslimat</Typography>
            </Box>
            <Chip label={selectedIslem?.islemNo || "NO YOK"} color="primary" sx={{fontWeight:'bold', backgroundColor:'white', color:'#1e293b', border: '1px solid #d4d8de'}} />
        </DialogTitle>
        <DialogContent className="tl-detail-dialog-content" sx={{ mt: 0, p:2.2, background: 'linear-gradient(180deg, #f8faff 0%, #eef3fb 100%)' }}>
            {selectedIslem && (
                <Grid container spacing={3}>
                    
                    {/* 1. MÜŞTERİ BİLGİLERİ */}
                    <Grid item xs={12} md={5}>
                        <Card elevation={0} sx={{height:'100%', borderRadius: 2.2, border: '1px solid #d9e3ef', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'}}>
                            <CardContent>
                                <Typography variant="subtitle2" color="primary" sx={{display:'flex', alignItems:'center', gap:1, mb:2, fontWeight:'bold'}}>
                                    <PersonIcon fontSize="small"/> MÜŞTERİ / FİRMA
                                </Typography>
                                <Divider sx={{mb:2}} />
                                <Typography variant="h6" fontWeight="bold" sx={{mb:1}}>
                                    {selectedIslem.musteri 
                                        ? `${selectedIslem.musteri.isim} ${selectedIslem.musteri.soyisim || ''}` 
                                        : (selectedIslem.firma ? selectedIslem.firma.firmaIsmi : 'Belirsiz')}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    <strong>İşlem Tarihi:</strong> {selectedIslem.timestamp?.toDate ? selectedIslem.timestamp.toDate().toLocaleString('tr-TR') : '-'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 2. ÜRÜN BİLGİLERİ */}
                    <Grid item xs={12} md={7}>
                        <Card elevation={0} sx={{height:'100%', borderRadius: 2.2, border: '1px solid #d9e3ef', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'}}>
                            <CardContent>
                                <Typography variant="subtitle2" color="primary" sx={{display:'flex', alignItems:'center', gap:1, mb:2, fontWeight:'bold'}}>
                                    <InfoIcon fontSize="small"/> ÜRÜN DETAYLARI
                                </Typography>
                                <Divider sx={{mb:2}} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Kategori</Typography>
                                        <Typography variant="body1" fontWeight="500">{selectedIslem.kategori}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Ürün</Typography>
                                        <Typography variant="body1" fontWeight="500">{selectedIslem.urun}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Miktar</Typography>
                                        <Typography variant="body1" fontWeight="500">
                                            {selectedIslem.gram ? `${selectedIslem.gram} Gr` : ''} 
                                            {selectedIslem.gram && selectedIslem.adet ? ' / ' : ''} 
                                            {selectedIslem.adet ? `${selectedIslem.adet} Adet` : ''}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="textSecondary" display="block">Barkod</Typography>
                                        <Typography variant="body1" fontWeight="500">{selectedIslem.barkod || '-'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="textSecondary" display="block">Ürün Notu / Açıklama</Typography>
                                        <Box sx={{ p:1, backgroundColor:'#f1f5f9', borderRadius:1, mt:0.5 }}>
                                            <Typography variant="body2" sx={{fontStyle:'italic'}}>
                                                {selectedIslem.urunBilgisi || selectedIslem.islemAciklamasi || 'Açıklama yok.'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 3. ÖDEME DETAYLARI */}
                    <Grid item xs={12}>
                        <Card elevation={0} sx={{borderRadius: 2.2, border: '1px solid #d9e3ef', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'}}>
                            <CardContent>
                                <Typography variant="subtitle2" color="primary" sx={{display:'flex', alignItems:'center', gap:1, mb:2, fontWeight:'bold'}}>
                                    <PaymentIcon fontSize="small"/> FİNANSAL DETAYLAR
                                </Typography>
                                <Divider sx={{mb:2}} />
                                <Grid container spacing={3}>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="textSecondary">Birim (Has) Fiyat</Typography>
                                        <Typography variant="body1" fontWeight="bold">{formatCurrency(selectedIslem.hasFiyati)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="textSecondary">Nakit Ödeme</Typography>
                                        <Typography variant="body1">{formatCurrency(selectedIslem.nakit)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="textSecondary">Kredi Kartı</Typography>
                                        <Typography variant="body1">{formatCurrency(selectedIslem.krediKarti)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="textSecondary">Havale / EFT</Typography>
                                        <Typography variant="body1">{formatCurrency(selectedIslem.havaleEft)}</Typography>
                                    </Grid>
                                </Grid>
                                
                                <Box className="tl-financial-summary" sx={{ mt:3, p:2, backgroundColor:'#e0f2f1', borderRadius:2, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                    <Box>
                                        <Typography variant="body2" color="textSecondary">TOPLAM TUTAR</Typography>
                                        <Typography variant="h5" color="#00695c" fontWeight="bold">
                                            {formatCurrency(selectedIslem.toplamTutar)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign:'right' }}>
                                        <Typography variant="body2" color="error">Kalan Bakiye (Borç)</Typography>
                                        <Typography variant="h6" color="error" fontWeight="bold">
                                            {formatCurrency(selectedIslem.bakiye)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 4. TESLİMAT PLANLAMA */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{p:3, borderRadius: 2.2, border: '1px solid #d9e3ef', borderLeft: '6px solid #1e293b', boxShadow: '0 6px 14px rgba(15, 23, 42, 0.08)'}}>
                            <Typography variant="h6" color="#1e293b" gutterBottom sx={{display:'flex', alignItems:'center', fontWeight:'bold'}}>
                                <CalendarTodayIcon sx={{ mr: 1 }} />
                                Teslimat Planlaması
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                Bu ürünün teslim edileceği tarihi aşağıdan seçerek <b>Ajanda</b> sistemine otomatik işlenmesini sağlayabilirsiniz.
                            </Typography>

                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <TextField
                                        className="tl-date-field"
                                        label="Planlanan Teslimat Tarihi"
                                        type="date"
                                        fullWidth
                                        value={planlananTarih}
                                        onChange={(e) => setPlanlananTarih(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        sx={{backgroundColor:'white'}}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button 
                                        onClick={handleDateSave} 
                                        variant="contained" 
                                        color="primary"
                                        fullWidth
                                        size="large"
                                        startIcon={<SaveIcon />}
                                        sx={{height:'56px', fontWeight:'bold', backgroundColor:'#1e293b'}}
                                    >
                                        KAYDET
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 1.6, borderTop:'1px solid #e5e7eb', backgroundColor: '#f3f6fb' }}>
            <Button onClick={() => setOpenDetailModal(false)} color="inherit" variant="outlined" sx={{fontWeight:'bold', borderRadius: '8px'}}>
                Kapat
            </Button>
        </DialogActions>
      </Dialog>

      {/* --- TESLİMAT ONAY MODALI (CONFIRM) --- */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{ className: 'tl-confirm-dialog', sx: { borderRadius: 2.5, border: '1px solid #dce5f1', boxShadow: '0 14px 28px rgba(15,23,42,0.2)' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1e3a8a', background: 'linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)' }}>
          Teslimat Onayı
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu ürünü müşteriye teslim ettiğinizi ve <b>stoğun şimdi düşüleceğini</b> onaylıyor musunuz?
            <br/><br/>
            <i>Bu işlem geri alınamaz ve kayıt "Teslim Edildi" olarak güncellenecektir.</i>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc' }}>
          <Button onClick={() => setOpenConfirmDialog(false)} color="inherit" sx={{ borderRadius: '8px' }}>İptal</Button>
          <Button 
            onClick={confirmTeslimat} 
            variant="contained" 
            color="success" 
            autoFocus 
            disabled={confirmLoading}
            startIcon={confirmLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ borderRadius: '8px', background: 'linear-gradient(135deg, #1aa06f 0%, #0f7a55 100%)' }}
          >
            {confirmLoading ? "İşleniyor..." : "Evet, Teslim Et"}
          </Button>
        </DialogActions>
      </Dialog>

      <style>
        {`
          /* --- MOBİL: Teslimat Listesi (masaüstü sx stillerine dokunulmaz) --- */
          @media (max-width: 560px) {
            .tl-page {
              height: auto !important;
              min-height: 0 !important;
              overflow-x: hidden !important;
              overflow-y: visible !important;
              padding: 10px 8px !important;
              box-sizing: border-box !important;
            }

            .tl-header {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 8px !important;
              padding: 10px !important;
              margin-bottom: 10px !important;
            }

            .tl-header-main {
              width: 100% !important;
            }

            .tl-header .MuiTypography-subtitle1 {
              font-size: 0.95rem !important;
              line-height: 1.25 !important;
            }

            .tl-header .MuiTypography-caption {
              font-size: 0.68rem !important;
            }

            .tl-header-chip {
              align-self: flex-start !important;
              font-size: 0.74rem !important;
              height: 24px !important;
            }

            .tl-table-wrap {
              flex: none !important;
              overflow-x: auto !important;
              overflow-y: visible !important;
              -webkit-overflow-scrolling: touch;
            }

            /* 620px'lik tablo 323px'lik alanda yana kaydiriliyordu; satir
               tek ekrana siğan bloga donusuyor. */
            .tl-table {
              min-width: 0 !important;
              width: 100% !important;
              display: block !important;
            }

            .tl-table thead {
              display: none !important;
            }

            .tl-table tbody {
              display: block !important;
              width: 100% !important;
            }

            .tl-table tbody tr.tl-data-row {
              display: grid !important;
              grid-template-columns: minmax(0, 1fr) auto;
              gap: 2px 8px;
              padding: 8px 6px !important;
              border-bottom: 1px solid #e2e8f0 !important;
            }

            .tl-table tbody tr.tl-data-row > .MuiTableCell-root {
              display: block !important;
              width: auto !important;
              max-width: none !important;
              padding: 0 !important;
              border: none !important;
            }

            .tl-c-musteri {
              grid-column: 1 !important;
              grid-row: 1 !important;
            }

            .tl-c-durum {
              grid-column: 2 !important;
              grid-row: 1 !important;
              text-align: right !important;
            }

            .tl-c-urun {
              grid-column: 1 !important;
              grid-row: 2 !important;
            }

            .tl-c-miktar {
              grid-column: 2 !important;
              grid-row: 2 !important;
              text-align: right !important;
              white-space: nowrap !important;
            }

            .tl-c-tarih {
              grid-column: 1 !important;
              grid-row: 3 !important;
            }

            .tl-c-planlanan {
              grid-column: 2 !important;
              grid-row: 3 !important;
              text-align: right !important;
            }

            .tl-c-aksiyon {
              grid-column: 1 / -1 !important;
              grid-row: 4 !important;
              text-align: right !important;
              margin-top: 4px !important;
            }

            .tl-table .MuiTableCell-root {
              padding: 5px 5px !important;
              font-size: 12px !important;
              word-break: break-word;
            }

            .tl-table .MuiTypography-caption {
              font-size: 12px !important;
            }

            .tl-table .MuiChip-root {
              max-width: 100%;
            }

            .tl-table .MuiChip-label {
              font-size: 11px !important;
              padding-left: 6px !important;
              padding-right: 6px !important;
            }

            .tl-teslim-btn {
              font-size: 11px !important;
              padding: 4px 8px !important;
              min-width: 0 !important;
            }

            .tl-teslim-btn .MuiButton-startIcon {
              margin-right: 4px !important;
            }

            .tl-teslim-btn .MuiSvgIcon-root {
              font-size: 16px !important;
            }

            .tl-detail-dialog {
              width: 100% !important;
              max-width: 100% !important;
              max-height: 100svh !important;
              margin: 0 !important;
            }

            .tl-detail-dialog-title {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 10px !important;
              padding: 14px !important;
            }

            .tl-detail-dialog-title .MuiTypography-h6 {
              font-size: 1rem !important;
            }

            .tl-detail-dialog-content {
              padding: 12px !important;
            }

            .tl-detail-dialog-content .MuiGrid-container {
              margin-top: 0 !important;
            }

            .tl-detail-dialog-content .MuiGrid-item {
              padding-top: 8px !important;
              padding-bottom: 8px !important;
            }

            .tl-detail-dialog-content .MuiCardContent-root {
              padding: 14px !important;
            }

            .tl-detail-dialog-content .MuiTypography-h6 {
              font-size: 1.05rem !important;
            }

            .tl-financial-summary {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: 12px !important;
              padding: 14px !important;
            }

            .tl-financial-summary > div:last-child {
              text-align: left !important;
            }

            .tl-date-field input {
              font-size: 16px !important;
            }

            .tl-detail-dialog-content .MuiDialogActions-root,
            .tl-detail-dialog + .MuiDialogActions-root {
              padding: 12px !important;
            }

            .tl-confirm-dialog {
              width: min(calc(100vw - 48px), 360px) !important;
              max-width: min(calc(100vw - 48px), 360px) !important;
              margin: 16px !important;
            }

            .tl-confirm-dialog .MuiDialogContent-root {
              padding: 12px 16px !important;
            }

            .tl-confirm-dialog .MuiDialogActions-root {
              flex-wrap: wrap !important;
              gap: 8px !important;
              justify-content: stretch !important;
            }

            .tl-confirm-dialog .MuiDialogActions-root .MuiButton-root {
              flex: 1 1 calc(50% - 4px) !important;
              min-width: 0 !important;
            }
          }
        `}
      </style>

    </Box>
  );
}

export default TeslimatListesi;